import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AtSign, ChevronRight, ExternalLink, FileText, Globe, Link2 } from 'lucide-react';
import OrganizerVerifiedBadge from './OrganizerVerifiedBadge';

/**
 * Unterer Teil der Event-Seite auf dem Handy (26.10.2026). Feste Reihenfolge, jeder Abschnitt mit
 * einer kleinen Überschrift in Leseschrift, alles als Liste statt als Kartenstapel:
 *
 * 1. **Beschreibung** mit „Mehr anzeigen“.
 * 2. **Links** als Zeilen mit Symbol und Pfeil wie bei AllTrails (Ausschreibung, Website, Flyer, Kanäle).
 * 3. **Veranstalter** als eine Zeile wie bei Luma und Eventbrite, darunter „Ich bin der Veranstalter“,
 *    solange den Verein niemand pflegt.
 * 4. **Übernachten** (Partnerlink, Kennzeichnung bleibt) und **Artikel zum Event** als Zeilen.
 *
 * Die Strecken stehen nicht hier, sondern im Kopf direkt unter der Karte, die sie steuern
 * (`EventDetailKopf`, Prop `strecken`): weiter unten sah man die Wirkung eines Tipps nicht.
 *
 * Was danach kommt (Preisstufen, Zahlen für Redaktion und Veranstalter), bleibt in der Seite.
 *
 * Am Rechner steht derselbe Teil unter dem Strecken-Block (`EventDetailDesktop`), dort mit dem
 * **Flyer als Bild** und ohne Unterkunft (die steht dort rechts neben der Anmeldung). Jeder
 * Abschnitt trägt eine Id (`ABSCHNITT_IDS`), die Leiste oben springt dorthin.
 */
export interface UntenLink { key: string | number; label: string; href: string; art: 'dokument' | 'web' | 'kanal' | 'link'; onClick?: () => void }

export interface UntenVeranstalter {
  name: string;
  avatar: React.ReactNode;
  href?: string;
  verifiziert?: boolean;
  claim?: () => void;
}

interface Props {
  beschreibung?: string | null;
  links: UntenLink[];
  /** Übernachten (Partnerlink), schon als Zeile gezeichnet; `null`, wenn es keine gibt. */
  unterkunft?: React.ReactNode;
  /** Artikel, die auf dieses Event zeigen. */
  artikel?: { key: string | number; href: string; titel: string; meta: string; bild?: string | null; onClick?: () => void }[];
  /** Flyer als Bild statt als Link; nur am Rechner. Dann fehlt er in `links`. */
  flyerBild?: { url: string; alt: string } | null;
  /** Auf dem Handy hier; am Rechner rechts neben der Anmeldung (`EventVeranstalterKarte`). */
  veranstalter?: UntenVeranstalter | null;
}

const ICONS = { dokument: FileText, web: Globe, kanal: AtSign, link: Link2 } as const;

/** Anker der Abschnitte, für die Sprungleiste am Rechner. */
export const ABSCHNITT_IDS = {
  ueber: 'event-ueber', links: 'event-links', flyer: 'event-flyer',
  veranstalter: 'event-veranstalter', unterkunft: 'event-unterkunft', artikel: 'event-artikel',
} as const;

const Abschnitt: React.FC<{ titel: string; id: string; children: React.ReactNode }> = ({ titel, id, children }) => (
  <section className="edu-abschnitt" id={id}>
    <h2 className="edu-titel">{titel}</h2>
    {children}
  </section>
);

/** Veranstalter als Zeile, darunter „Bist du der Veranstalter?“, solange den Verein niemand pflegt. */
export const VeranstalterZeilen: React.FC<{ veranstalter: UntenVeranstalter }> = ({ veranstalter }) => (
  <div className="edu-liste edu-liste--zeilen">
    {veranstalter.href ? (
      <Link className="edu-zeile edu-zeile--gross" to={veranstalter.href}>
        {veranstalter.avatar}
        <span className="edu-zeile-text">
          <strong>{veranstalter.name}<OrganizerVerifiedBadge verified={veranstalter.verifiziert} compact /></strong>
          <span>Alle Events und Infos</span>
        </span>
        <ChevronRight size={18} aria-hidden className="edu-pfeil" />
      </Link>
    ) : (
      <div className="edu-zeile edu-zeile--gross">
        {veranstalter.avatar}
        <span className="edu-zeile-text"><strong>{veranstalter.name}</strong></span>
      </div>
    )}
    {veranstalter.claim && (
      <button type="button" className="edu-zeile edu-zeile--claim" onClick={veranstalter.claim}>
        <span className="edu-zeile-text">
          <strong>Bist du der Veranstalter?</strong>
          <span>Trag deine Events selbst ein und ändere sie, wann du willst. Kostenlos.</span>
        </span>
        <ChevronRight size={18} aria-hidden className="edu-pfeil" />
      </button>
    )}
  </div>
);

const EventDetailUnten: React.FC<Props> = ({ beschreibung, links, veranstalter, unterkunft, artikel, flyerBild }) => {
  const [offen, setOffen] = useState(false);
  const lang = (beschreibung?.length ?? 0) > 220;

  return (
    <div className="edu">
      {beschreibung && (
        <Abschnitt titel="Über das Event" id={ABSCHNITT_IDS.ueber}>
          <p className={`edu-beschreibung${lang && !offen ? ' edu-beschreibung--zu' : ''}`}>{beschreibung}</p>
          {lang && (
            <button type="button" className="edu-mehr" onClick={() => setOffen(v => !v)}>{offen ? 'Weniger anzeigen' : 'Mehr anzeigen'}</button>
          )}
        </Abschnitt>
      )}

      {links.length > 0 && (
        <Abschnitt titel="Links" id={ABSCHNITT_IDS.links}>
          <div className="edu-liste edu-liste--zeilen">
            {links.map(l => {
              const Icon = ICONS[l.art];
              return (
                <a key={l.key} className="edu-zeile" href={l.href} target="_blank" rel="noopener noreferrer" onClick={l.onClick}>
                  <span className="edu-zeile-icon" aria-hidden><Icon size={17} /></span>
                  <span className="edu-zeile-text">{l.label}</span>
                  <ExternalLink size={16} aria-hidden className="edu-pfeil" />
                </a>
              );
            })}
          </div>
        </Abschnitt>
      )}

      {flyerBild && (
        <Abschnitt titel="Flyer" id={ABSCHNITT_IDS.flyer}>
          <a className="edu-flyer" href={flyerBild.url} target="_blank" rel="noopener noreferrer"><img src={flyerBild.url} alt={flyerBild.alt} /></a>
        </Abschnitt>
      )}

      {veranstalter && (
        <Abschnitt titel="Veranstalter" id={ABSCHNITT_IDS.veranstalter}>
          <VeranstalterZeilen veranstalter={veranstalter} />
        </Abschnitt>
      )}

      {unterkunft && (
        <Abschnitt titel="Unterkunft" id={ABSCHNITT_IDS.unterkunft}>
          <div className="edu-liste edu-liste--zeilen">{unterkunft}</div>
        </Abschnitt>
      )}

      {artikel && artikel.length > 0 && (
        <Abschnitt titel="Artikel zum Event" id={ABSCHNITT_IDS.artikel}>
          <div className="edu-liste edu-liste--zeilen">
            {artikel.map(a => (
              <Link key={a.key} className="edu-zeile edu-zeile--gross" to={a.href} onClick={a.onClick}>
                <span className="edu-artikel-bild" style={a.bild ? { backgroundImage: `url('${a.bild}')` } : undefined} aria-hidden />
                <span className="edu-zeile-text"><span>{a.meta}</span><strong className="edu-artikel-titel">{a.titel}</strong></span>
                <ChevronRight size={18} aria-hidden className="edu-pfeil" />
              </Link>
            ))}
          </div>
        </Abschnitt>
      )}
    </div>
  );
};

export default EventDetailUnten;
